import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Briefcase, HandCoins, CreditCard, TrendingUp, AlertCircle, DollarSign, X } from 'lucide-react';
import { BrokerDashboard, SyndicatorDashboard, ISODashboard } from '../components/dashboards';
import { NotificationDashboard } from '../components/portfolio';
import { getDashboardRoles } from '../components/DashboardSelector';
import TenantBanner from '../components/TenantBanner';
import RefiPaymentReview from '../components/RefiPaymentReview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { getAuthHeaders, user, selectedDashboard } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalDeals: 0,
    activeDeals: 0,
    totalSyndications: 0,
    totalPayments: 0,
    totalCommissions: 0,
    syndicationCommissions: 0,
  });
  const [loading, setLoading] = useState(true);
  // 10/07 (Josh): the Welcome card sits at the top and can be closed with an X.
  // Dismissal is a per-browser convenience, remembered in localStorage.
  const WELCOME_KEY = 'pfs.welcome.dismissed';
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => {
    try { return window.localStorage.getItem(WELCOME_KEY) === '1'; } catch { return false; }
  });
  const dismissWelcome = () => {
    setWelcomeDismissed(true);
    try { window.localStorage.setItem(WELCOME_KEY, '1'); } catch { /* storage unavailable: hide for this visit only */ }
  };
  const [commissionDateRange, setCommissionDateRange] = useState('all');
  const [syndicationDateRange, setSyndicationDateRange] = useState('all');

  const isAdmin = user?.roles?.includes('admin');
  const dashboardOptions = getDashboardRoles(user?.roles);
  const hasMultipleRoles = dashboardOptions.length > 1;

  useEffect(() => {
    // Only fetch admin stats if user is admin
    if (!isAdmin) {
      setLoading(false);
      return;
    }

    const fetchStats = async () => {
      try {
        // Use the new dashboard-stats endpoint for accurate counts
        const statsResponse = await axios.get(`${API}/reports/dashboard-stats`, { headers: getAuthHeaders() }).catch(() => ({ data: {} }));
        const dashboardStats = statsResponse.data;
        
        const newStats = {
          totalUsers: dashboardStats.total_users || 0,
          totalDeals: dashboardStats.total_deals || 0,
          activeDeals: dashboardStats.active_deals || 0,
          totalSyndications: dashboardStats.total_syndications || 0,
          totalPayments: dashboardStats.total_transactions || 0,
          totalCommissions: 0,
          syndicationCommissions: dashboardStats.syndication_earnings || 0,
        };
        
        setStats(newStats);
      } catch (error) {
        console.error('Failed to fetch stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, [getAuthHeaders, user, isAdmin]);

  // Render role-based dashboard for non-admin users
  const renderRoleBasedDashboard = () => {
    // If user has no dashboard options (edge case), show error state
    if (dashboardOptions.length === 0 && !isAdmin) {
      return (
        <Card>
          <CardContent className="py-12 text-center">
            <AlertCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No dashboard available for your account.</p>
            <p className="text-sm text-gray-400 mt-2">Contact an administrator if you believe this is an error.</p>
          </CardContent>
        </Card>
      );
    }

    // Render based on selected dashboard type
    switch (selectedDashboard) {
      case 'broker':
        return <BrokerDashboard />;
      case 'syndicator':
        return <SyndicatorDashboard />;
      case 'iso':
        return <ISODashboard />;
      case 'funder':
        // Funder uses same view as broker for now
        return <BrokerDashboard />;
      case 'merchant':
        // Merchant has limited view
        return <BrokerDashboard />;
      case 'underwriter':
        // Underwriter uses broker view
        return <BrokerDashboard />;
      default:
        // Fallback to first available dashboard
        if (user?.roles?.includes('syndicator') || user?.roles?.includes('investor')) {
          return <SyndicatorDashboard />;
        }
        if (user?.roles?.includes('broker')) {
          return <BrokerDashboard />;
        }
        if (user?.roles?.includes('iso')) {
          return <ISODashboard />;
        }
        return <BrokerDashboard />;
    }
  };

  // Admin dashboard stat cards
  const statCards = [
    {
      title: 'Total Users',
      value: stats.totalUsers,
      icon: Users,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      hoverBorder: 'hover:border-blue-300',
      hoverBg: 'group-hover:bg-blue-50/50',
      link: '/users',
    },
    {
      title: 'Total Deals',
      value: stats.totalDeals,
      icon: Briefcase,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      hoverBorder: 'hover:border-green-300',
      hoverBg: 'group-hover:bg-green-50/50',
      link: '/deals',
    },
    {
      title: 'Active Deals',
      value: stats.activeDeals,
      icon: TrendingUp,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      hoverBorder: 'hover:border-purple-300',
      hoverBg: 'group-hover:bg-purple-50/50',
      link: '/deals?status=Funded',
    },
    {
      title: 'Total Syndications',
      value: stats.totalSyndications,
      icon: HandCoins,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
      hoverBorder: 'hover:border-orange-300',
      hoverBg: 'group-hover:bg-orange-50/50',
      link: '/syndication-dashboard',
    },
    {
      title: 'Payments',
      value: stats.totalPayments,
      icon: CreditCard,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      hoverBorder: 'hover:border-indigo-300',
      hoverBg: 'group-hover:bg-indigo-50/50',
      link: '/deals',
    },
  ];

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Tenant Banner - shows when on validation or when live is empty (only after loading) */}
        {!loading && <TenantBanner dealCount={stats.totalDeals} />}
        
        {/* Non-admin users see role-based dashboards */}
        {!isAdmin ? (
          <>
            <h1 className="text-3xl font-bold text-gray-900 mb-6" data-testid="dashboard-title">
              Dashboard
            </h1>
            {renderRoleBasedDashboard()}
          </>
        ) : (
          /* Admin Dashboard */
          <>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
              <h1 className="text-3xl font-bold text-gray-900" data-testid="dashboard-title">
                Admin Dashboard
              </h1>

              {/* Time filter for commissions/syndications */}
              <div className="mt-4 md:mt-0 flex items-center space-x-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/daily-import-review')}
                  className="flex items-center gap-1.5 text-blue-700 border-blue-200 hover:bg-blue-50"
                  data-testid="todays-activity-btn"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                  Today's Activity
                </Button>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Commissions</span>
                  <Select value={commissionDateRange} onValueChange={setCommissionDateRange}>
                    <SelectTrigger className="w-32 h-9">
                      <SelectValue placeholder="Range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All time</SelectItem>
                      <SelectItem value="month">Last 30 days</SelectItem>
                      <SelectItem value="week">Last 7 days</SelectItem>
                      <SelectItem value="day">Today</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Syndication</span>
                  <Select value={syndicationDateRange} onValueChange={setSyndicationDateRange}>
                    <SelectTrigger className="w-32 h-9">
                      <SelectValue placeholder="Range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All time</SelectItem>
                      <SelectItem value="month">Last 30 days</SelectItem>
                      <SelectItem value="week">Last 7 days</SelectItem>
                      <SelectItem value="day">Today</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : (
              <>
                {!welcomeDismissed && (
                  <Card className="mb-6 relative" data-testid="welcome-card">
                    <button
                      type="button"
                      onClick={dismissWelcome}
                      aria-label="Close welcome message"
                      title="Close"
                      className="absolute right-3 top-3 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                      data-testid="welcome-close"
                    >
                      <X className="h-4 w-4" />
                    </button>
                    <CardHeader>
                      <CardTitle>Welcome to Power Funding Solutions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">
                        Payment Finance System - Manage deals, syndications, and payments with comprehensive validation.
                      </p>
                      <div className="mt-4 flex items-start space-x-2 text-sm text-gray-600">
                        <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                        <p>
                          All data entries are validated according to strict business rules. Invalid data will be rejected with detailed error messages.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* 10/07 (Josh): the digest + trends cards moved to the Portfolio page. */}
                <RefiPaymentReview />
                <NotificationDashboard />
              </>
            )}
          </>
        )}
      </div>
    </Layout>
  );
};

export default Dashboard;
